import React from "react";
import { Grid, Paper } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";


const useStyles = makeStyles((theme) => ({
    root: {
        height: "100vh",
    },
    image: {
        backgroundImage:
            "url(https://images.unsplash.com/photo-1530274083826-2facadf42457?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1268&q=80%27)",
        backgroundRepeat: "no-repeat",
        backgroundColor: theme.palette.background.default,
        backgroundSize: "cover",
        backgroundPosition: "center",
    },
    paper: {
        margin: theme.spacing(8, 4),
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "justify",
    },
    copyrights: {
        position: "absolute",
        bottom: "5px",
    },
}));


export const PrivacyPolicy = () => {
    const classes = useStyles();

    return (
        <Grid container component="main" className={classes.root}>
            <Grid item xs={false} sm={4} md={4} className={classes.image}/>
            <Grid item xs={12} sm={8} md={8} component={Paper} elevation={6} square>
                <div className={classes.paper}>
                    <h1>Privacy Policy</h1>
                    <p>Last updated: October 8, 2026</p>
                    <p>PlanPraise ("we", "us", "our") is a tool for worship teams to plan events,
                        organize their song catalog, and propose and vote on new songs to learn. This
                        Privacy Policy explains what information we collect through the Service,
                        why we collect it, and how it's used.</p>

                    <h2>Information We Collect</h2>
                    <h3>Account information</h3>
                    <p>When you register, we collect your email address and name. If you sign in
                        with Google or Microsoft instead of a password, that provider
                        gives us your email address, name, and profile information (limited to
                        basic profile/email scopes) so we can create and authenticate your
                        account - we don't receive your password for those services, and we don't
                        post to those accounts on your behalf.</p>
                    <h3>Team, song, and event data</h3>
                    <p>Once you join a team, we store your team membership and role (member, team
                        leader, or team admin), and any songs you propose, votes you cast, and
                        events you're marked as attending.</p>
                    <h3>Spotify integration (optional)</h3>
                    <p>A team admin may optionally connect their team to Spotify to automatically
                        create and update playlists for upcoming events. If connected, we store an
                        access token and refresh token for that Spotify account, used only to
                        manage playlists for that team's events. A team admin can disconnect
                        Spotify at any time from the team admin page, which removes these tokens.</p>
                    <h3>Notifications and activity</h3>
                    <p>If you turn on push notifications, we store your browser's push
                        subscription so we can send you event updates; you can turn this off at
                        any time in Account Settings. We also record when you last signed in,
                        which team leaders can see next to your name in your team's member list.</p>
                    <h3>Usage data</h3>
                    <p>Like most web services, our hosting infrastructure automatically logs
                        standard technical data such as IP address, browser type, and request
                        timestamps, used only for security and troubleshooting.</p>

                    <h2>How We Use Your Information</h2>
                    <ul>
                        <li>To create and authenticate your account, and let you sign in.</li>
                        <li>To show you and your teammates the songs, events, and set lists
                            belonging to your team.</li>
                        <li>To let team leaders/admins manage the team's catalog, events, and (if
                            connected) Spotify playlists.</li>
                        <li>To contact you about your account or the Service, if needed.</li>
                    </ul>

                    <h2>Who Can See Your Information</h2>
                    <p>Your name, the songs you propose, and your votes are visible to other
                        members of your own team - that's how the catalog and song-proposal
                        features work. We do not sell your information, and we do not share it
                        with third parties except the service providers necessary to run
                        PlanPraise itself (our hosting provider, and Google/Microsoft/
                        Spotify, solely for the sign-in and playlist features described above).</p>

                    <h2>Data Retention and Deletion</h2>
                    <p>We keep your account and team data for as long as your account is active,
                        so the app can keep working for your team. You are in control of it:</p>
                    <h3>Leaving a team</h3>
                    <p>You can leave a team yourself from Account Settings, and a team leader can
                        remove a member from the team. Either way you lose access to that team's
                        songs and events, but your account stays and you can be invited back.</p>
                    <h3>Erasing your personal data</h3>
                    <p>From Account Settings you can ask us to erase your personal data. We email
                        a confirmation link to your address, and once you confirm, your name,
                        email address, sign-in details (including linked Google or Microsoft
                        accounts), push notification subscriptions, and team memberships are
                        permanently removed or irreversibly masked, and you can no longer sign in
                        with that account. This cannot be undone.</p>
                    <p>What remains afterwards: songs your team added and votes cast stay as part
                        of your team's records, no longer linked to your name, and we keep a
                        record that an erasure took place (it contains no personal information).
                        Text that people typed into your team's own content, such as an event
                        note that happens to mention you, is team content and is not removed
                        automatically; ask a team leader to edit it, or contact us.</p>
                    <p>Our hosting provider keeps backups of our database and service logs for a
                        limited period before they are deleted, so erased information can persist
                        in those for a short time before it expires.</p>
                    <p>You can also ask us to do this by emailing the addresses below.</p>

                    <h2>Your Rights</h2>
                    <p>Depending on where you live (for example in the European Economic Area
                        under the GDPR), you have the right to access the personal data we hold
                        about you, to have it corrected, to have it erased, and to object to how
                        it's used. You can download a copy of your data from Account Settings and erase
                        it yourself as described above; for
                        anything else, contact us and we'll respond within one month.</p>

                    <h2>Security</h2>
                    <p>We take reasonable measures to protect your information, including
                        encrypting connections to the Service (HTTPS) and never storing your
                        password when you use social sign-in. No method of transmission or storage
                        is ever 100% secure, so we can't guarantee absolute security.</p>

                    <h2>Children's Privacy</h2>
                    <p>PlanPraise is not directed at children under 13, and we do not knowingly
                        collect personal information from anyone under 13. If you believe a child
                        has provided us with personal data, please contact us and we'll remove it.</p>

                    <h2>Changes to This Policy</h2>
                    <p>We may update this Privacy Policy from time to time. Changes will be posted
                        on this page with an updated "Last updated" date.</p>

                    <h2>Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy, or want to request
                        deletion of your data, contact us:</p>
                    <ul>
                        <li>By email: filiphenriklarsen@gmail.com</li>
                        <li>By email: elraphah@gmail.com</li>
                    </ul>
                </div>
            </Grid>
        </Grid>
);
};
